import type { SortDirection, TableSortState } from '../../utils/listOrdering';

/** Join the values a row should match, for a case-insensitive contains search. */
export function rowSearchText(values: unknown[]): string {
  return values
    .filter((value) => value != null && value !== false)
    .map((value) => String(value))
    .join(' ')
    .toLowerCase();
}

/** Empty values sort last in either direction. Numbers and booleans stay numeric. */
export function compareTableValues(left: unknown, right: unknown): number {
  const leftEmpty = left == null || left === '';
  const rightEmpty = right == null || right === '';
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function filterAndSortRows<Row, Key extends string>(
  rows: readonly Row[],
  query: string,
  searchText: (row: Row) => string,
  sort: TableSortState<Key>,
  sortValue: (row: Row, key: Key) => unknown,
): Row[] {
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? rows.filter((row) => searchText(row).toLowerCase().includes(needle))
    : [...rows];
  if (!sort.key) return filtered;
  const direction = sort.direction === 'desc' ? -1 : 1;
  const key = sort.key;
  return [...filtered].sort((left, right) => {
    const leftValue = sortValue(left, key);
    const rightValue = sortValue(right, key);
    const compared = compareTableValues(leftValue, rightValue);
    const leftEmpty = leftValue == null || leftValue === '';
    const rightEmpty = rightValue == null || rightValue === '';
    if (leftEmpty || rightEmpty) return compared;
    return compared * direction;
  });
}

export type { SortDirection };
